import { c as _c } from "react/compiler-runtime";
import { Box, HStack, Progress, Text, VStack } from '@chakra-ui/react';
import { AnalyzingIllustration } from '@src/components/sco/Funding/FundingLandingDiagnostic/AnalyzingIllustration';
import { DiagnosticColumns } from '@src/components/sco/Funding/FundingLandingDiagnostic/DiagnosticShell';
import { AccentHeading, CheckRow, Panel, ProofStats } from '@src/components/sco/Funding/FundingLandingDiagnostic/shared';
import { useRequestFunnelContext } from '@src/components/sco/RequestFunnel/RequestFunnelContext';
import tracker from '@src/lib/tracker';
import React, { useCallback, useEffect, useRef, useState } from 'react';

/** Match the original HTML pacing so SVG loops can play through. */
const MOMENT_DURATION_MS = 4500;

/** Fast-start, slow-end for segment progress fill. `t` in [0, 1] → [0, 1]. */
function easeOutCubic(t: number): number {
  const clamped = Math.min(1, Math.max(0, t));
  return 1 - (1 - clamped) ** 3;
}
const MOMENTS = [{
  h1: 'Never pitch a',
  h2: 'dead end.',
  beats: ['Every investor is hand-verified', 'Zero stale contacts', 'Zero closed funds'],
  line: 'Scanning 22,421 hand-verified investors…'
}, {
  h1: 'Only pitch people',
  h2: 'who answer.',
  beats: ['We monitor who responds fastest', 'Investors ranked by response rate', 'No wasted outreach'],
  line: 'Finding the most responsive contacts…'
}, {
  h1: 'Your pitch,',
  h2: 'written for you.',
  beats: ['Every outbound email is customized', 'AI-assist tells you what Investors want to hear', 'No more mass spamming (it never works!)'],
  line: 'Learning what each investor responds to…'
}, {
  h1: 'We pitch.',
  h2: 'You build.',
  beats: ['Our system automatically sends for you', 'Emails come from your own email client', 'Timed emails avoid spam filters'],
  line: 'Setting up your automated outreach…'
}, {
  h1: 'Always know',
  h2: 'where you stand.',
  beats: ['We report every action in real time', 'Who Opens, Replies, Ignores', 'Know exactly what your next move is'],
  line: 'Building your raise dashboard…'
}] as const;
export const AnalyzingStep: React.FC = () => {
  const $ = _c(80);
  const {
    setStep
  } = useRequestFunnelContext();
  const [index, setIndex] = useState(0);
  const [momentStartedAt, setMomentStartedAt] = useState(_temp);
  const [progressTick, setProgressTick] = useState(0);
  const advancedForIndexRef = useRef(null);
  const moment = MOMENTS[index];
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  useEffect(_temp2, t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      advancedForIndexRef.current = null;
      setMomentStartedAt(Date.now());
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== index) {
    t2 = [index];
    $[2] = index;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] !== index || $[5] !== setStep) {
    t3 = () => {
      if (advancedForIndexRef.current === index) {
        return;
      }
      advancedForIndexRef.current = index;
      if (index >= MOMENTS.length - 1) {
        setStep("results");
      } else {
        setIndex(_temp3);
      }
    };
    $[4] = index;
    $[5] = setStep;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const goNext = t3;
  let t4;
  if ($[7] !== goNext) {
    t4 = () => {
      const timeout = setTimeout(() => {
        goNext();
      }, MOMENT_DURATION_MS);
      return () => clearTimeout(timeout);
    };
    $[7] = goNext;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== goNext || $[10] !== index) {
    t5 = [goNext, index];
    $[9] = goNext;
    $[10] = index;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      const interval = setInterval(() => setProgressTick(Date.now()), 50);
      return () => clearInterval(interval);
    };
    t7 = [];
    $[12] = t6;
    $[13] = t7;
  } else {
    t6 = $[12];
    t7 = $[13];
  }
  useEffect(t6, t7);
  let t8;
  if ($[14] !== goNext) {
    t8 = () => {
      goNext();
    };
    $[14] = goNext;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const skipViaImage = t8;
  let T0;
  let T1;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t9;
  if ($[16] !== index || $[17] !== moment.beats || $[18] !== moment.h1 || $[19] !== moment.h2 || $[20] !== moment.line || $[21] !== momentStartedAt || $[22] !== progressTick || $[23] !== skipViaImage) {
    let t15;
    if ($[32] !== index || $[33] !== momentStartedAt || $[34] !== progressTick) {
      t15 = (_, i_0) => {
        if (i_0 < index) {
          return 100;
        }
        if (i_0 > index) {
          return 0;
        }
        const segmentElapsed = Math.max(0, progressTick - momentStartedAt);
        if (segmentElapsed >= MOMENT_DURATION_MS) {
          return 100;
        }
        return easeOutCubic(segmentElapsed / MOMENT_DURATION_MS) * 100;
      };
      $[32] = index;
      $[33] = momentStartedAt;
      $[34] = progressTick;
      $[35] = t15;
    } else {
      t15 = $[35];
    }
    const progressSegments = MOMENTS.map(t15);
    T1 = DiagnosticColumns;
    let t16;
    if ($[36] !== moment.h1 || $[37] !== moment.h2) {
      t16 = <AccentHeading accent={moment.h2} line1={moment.h1} />;
      $[36] = moment.h1;
      $[37] = moment.h2;
      $[38] = t16;
    } else {
      t16 = $[38];
    }
    let t17;
    if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
      t17 = <Text color="fg.subtle" fontSize="sm" fontWeight="bold" letterSpacing="0.1em" textAlign="start" textTransform="uppercase">Why it Works</Text>;
      $[39] = t17;
    } else {
      t17 = $[39];
    }
    let t18;
    if ($[40] !== index || $[41] !== moment.beats) {
      let t19;
      if ($[43] !== index) {
        t19 = beat => <CheckRow key={`${index}-${beat}`}>{beat}</CheckRow>;
        $[43] = index;
        $[44] = t19;
      } else {
        t19 = $[44];
      }
      t18 = moment.beats.map(t19);
      $[40] = index;
      $[41] = moment.beats;
      $[42] = t18;
    } else {
      t18 = $[42];
    }
    let t19;
    if ($[45] !== t18) {
      t19 = <VStack align="stretch" alignSelf="stretch" gap={2} width="full">{t17}{t18}</VStack>;
      $[45] = t18;
      $[46] = t19;
    } else {
      t19 = $[46];
    }
    let t20;
    if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
      t20 = <ProofStats />;
      $[47] = t20;
    } else {
      t20 = $[47];
    }
    if ($[48] !== t16 || $[49] !== t19) {
      t14 = <>{t16}{t19}{t20}</>;
      $[48] = t16;
      $[49] = t19;
      $[50] = t14;
    } else {
      t14 = $[50];
    }
    const t21 = index + 1;
    let t22;
    if ($[51] !== t21) {
      t22 = <Text color="colorPalette.fg" colorPalette="purple" fontSize="xs" fontWeight="bold" letterSpacing="widest" textTransform="uppercase">Finding your investors · Step {t21} of {MOMENTS.length}</Text>;
      $[51] = t21;
      $[52] = t22;
    } else {
      t22 = $[52];
    }
    let t23;
    if ($[53] !== skipViaImage) {
      t23 = e => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          skipViaImage();
        }
      };
      $[53] = skipViaImage;
      $[54] = t23;
    } else {
      t23 = $[54];
    }
    let t24;
    if ($[55] !== index) {
      t24 = <AnalyzingIllustration key={index} momentIndex={index} />;
      $[55] = index;
      $[56] = t24;
    } else {
      t24 = $[56];
    }
    let t25;
    if ($[57] !== skipViaImage || $[58] !== t23 || $[59] !== t24) {
      t25 = <Box aria-label="Skip to next step" cursor="pointer" marginY={1} role="button" tabIndex={0} width="full" onClick={skipViaImage} onKeyDown={t23}>{t24}</Box>;
      $[57] = skipViaImage;
      $[58] = t23;
      $[59] = t24;
      $[60] = t25;
    } else {
      t25 = $[60];
    }
    let t26;
    if ($[61] !== moment.line) {
      t26 = <Text color="fg" fontSize="sm" fontWeight="semibold">{moment.line}</Text>;
      $[61] = moment.line;
      $[62] = t26;
    } else {
      t26 = $[62];
    }
    if ($[63] !== t22 || $[64] !== t25 || $[65] !== t26) {
      t13 = <Panel>{t22}{t25}{t26}</Panel>;
      $[63] = t22;
      $[64] = t25;
      $[65] = t26;
      $[66] = t13;
    } else {
      t13 = $[66];
    }
    T0 = HStack;
    t9 = "orange";
    t10 = 2;
    t11 = "full";
    t12 = progressSegments.map(_temp4);
    $[16] = index;
    $[17] = moment.beats;
    $[18] = moment.h1;
    $[19] = moment.h2;
    $[20] = moment.line;
    $[21] = momentStartedAt;
    $[22] = progressTick;
    $[23] = skipViaImage;
    $[24] = T0;
    $[25] = T1;
    $[26] = t10;
    $[27] = t11;
    $[28] = t12;
    $[29] = t13;
    $[30] = t14;
    $[31] = t9;
  } else {
    T0 = $[24];
    T1 = $[25];
    t10 = $[26];
    t11 = $[27];
    t12 = $[28];
    t13 = $[29];
    t14 = $[30];
    t9 = $[31];
  }
  let t15;
  if ($[67] !== T0 || $[68] !== t10 || $[69] !== t11 || $[70] !== t12 || $[71] !== t9) {
    t15 = <T0 colorPalette={t9} gap={t10} width={t11}>{t12}</T0>;
    $[67] = T0;
    $[68] = t10;
    $[69] = t11;
    $[70] = t12;
    $[71] = t9;
    $[72] = t15;
  } else {
    t15 = $[72];
  }
  let t16;
  if ($[73] !== t13 || $[74] !== t15) {
    t16 = <>{t13}{t15}</>;
    $[73] = t13;
    $[74] = t15;
    $[75] = t16;
  } else {
    t16 = $[75];
  }
  let t17;
  if ($[76] !== T1 || $[77] !== t14 || $[78] !== t16) {
    t17 = <T1 left={t14} right={t16} />;
    $[76] = T1;
    $[77] = t14;
    $[78] = t16;
    $[79] = t17;
  } else {
    t17 = $[79];
  }
  return t17;
};
function _temp() {
  return Date.now();
}
function _temp2() {
  tracker.funnelStep(3);
}
function _temp3(i) {
  return i + 1;
}
function _temp4(value, i_1) {
  return <Progress.Root key={i_1} flex={1} size="sm" value={value}><Progress.Track bg="bg.muted" borderRadius="full"><Progress.Range bg="colorPalette.solid" transitionDuration="50ms" transitionProperty="width, inline-size" transitionTimingFunction="linear" /></Progress.Track></Progress.Root>;
}